<script setup>
import { computed, ref, watch, getCurrentInstance } from 'vue'
import { router } from '@inertiajs/vue3'

const props = defineProps({
    links:       { type: Array,  default: () => [] },
    currentPage: { type: Number, default: 1 },
    totalPages:  { type: Number, default: 1 },
    variant:     { type: String, default: 'admin' }, // 'admin' | 'staff'
})

const emit = defineEmits(['change'])

const instance         = getCurrentInstance()
const hasChangeListener = computed(() => !!instance?.vnode.props?.onChange)
const internalPage     = ref(props.currentPage)

watch(() => props.currentPage, val => { internalPage.value = val })

const hasBackendLinks       = computed(() => props.links.length > 0 && 'url' in props.links[0])
const currentPageForRender  = computed(() => internalPage.value)

// Active page button class driven by variant
const activeClass = computed(() =>
    props.variant === 'staff'
        ? 'bg-primary text-white border-primary'
        : 'bg-primary text-white border-primary'
)

// Hover class for inactive buttons
const hoverClass = computed(() =>
    props.variant === 'staff'
        ? 'hover:bg-primary/10 hover:text-primary hover:border-primary/30'
        : 'hover:bg-primary-50 hover:text-primary hover:border-primary-200'
)

function extractPage(url) {
    if (!url) return 1
    try {
        const params = new URL(url, window.location.origin).searchParams
        return parseInt(params.get('page')) || 1
    } catch {
        return 1
    }
}

function handleClick(page, url = null) {
    const newPage = url ? extractPage(url) : page
    if (hasChangeListener.value) {
        emit('change', newPage)
        internalPage.value = newPage
    } else if (hasBackendLinks.value && url) {
        router.get(url, {}, { preserveState: true, preserveScroll: true })
    } else {
        internalPage.value = newPage
    }
}
</script>

<template>
    <div
        v-if="(hasBackendLinks && links.filter(l => l.url).length > 1) || (!hasBackendLinks && totalPages > 1)"
        class="flex flex-wrap gap-1"
    >
        <!-- Backend Links -->
        <template v-if="hasBackendLinks">
            <template v-for="(link, index) in links" :key="index">
                <button
                    v-html="link.label"
                    class="px-3 py-1.5 border rounded-md text-xs font-medium transition-colors"
                    :class="[
                        link.active
                            ? activeClass
                            : !link.url
                                ? 'text-gray-400 cursor-not-allowed border-gray-200 bg-white'
                                : ['text-gray-600 border-gray-200 bg-white cursor-pointer', hoverClass]
                    ]"
                    :disabled="!link.url"
                    @click="link.url ? handleClick(null, link.url) : null"
                />
            </template>
        </template>

        <!-- Local Numeric Pagination -->
        <template v-else>
            <button
                @click="handleClick(currentPageForRender - 1)"
                :disabled="currentPageForRender === 1"
                class="px-3 py-1.5 border rounded-md text-xs font-medium transition-colors"
                :class="currentPageForRender === 1
                    ? 'text-gray-400 cursor-not-allowed border-gray-200 bg-white'
                    : ['text-gray-600 border-gray-200 bg-white', hoverClass]"
            >
                &laquo; Prev
            </button>

            <button
                v-for="page in totalPages" :key="page"
                @click="handleClick(page)"
                class="px-3 py-1.5 border rounded-md text-xs font-medium transition-colors"
                :class="currentPageForRender === page
                    ? activeClass
                    : ['text-gray-600 border-gray-200 bg-white', hoverClass]"
            >
                {{ page }}
            </button>

            <button
                @click="handleClick(currentPageForRender + 1)"
                :disabled="currentPageForRender === totalPages"
                class="px-3 py-1.5 border rounded-md text-xs font-medium transition-colors"
                :class="currentPageForRender === totalPages
                    ? 'text-gray-400 cursor-not-allowed border-gray-200 bg-white'
                    : ['text-gray-600 border-gray-200 bg-white', hoverClass]"
            >
                Next &raquo;
            </button>
        </template>
    </div>
</template>